/**
 * **数据包文件** —— 可选加载的节点包的文件格式与校验。
 *
 * ```jsonc
 * {
 *   "format": "node-print.package",
 *   "version": 1,
 *   "package": {
 *     "id": "function", "label": "函数", "kind": "domain",
 *     "categories": [{ "id": "function", "label": "函数" }],
 *     "nodeTypes": [{
 *       "type": "pkg.node", "title": "某节点",
 *       "category": "function", "width": 190, "keywords": ["if"],
 *       "rows": [ …行数据… ],
 *       "code": { "dialect": "expr", "source": "({ out: … })" }
 *     }]
 *   }
 * }
 * ```
 *
 * ## 为什么校验要写得这么细
 *
 * ⚠️ 这是一份**外来数据**（用户导入的文件、别人给的包），不是我们自己的代码。
 * 它进的是注册表，而注册表有两条硬约束：
 *   · 节点类型标识**不能重复**（重复直接抛错）；
 *   · 求值器必须能编译。
 *
 * 所以这里的策略是：**能在校验期挡掉的就别留到注册期**。
 * 校验的产出是一串**可读的问题**（而不是抛异常）—— 导入方要把它们
 * 显示给人看（「第 3 个节点缺 title」），抛异常只会得到一个堆栈。
 *
 * ## ⚠️ 数据包**不许**带 `evaluators` / `editors`
 *
 * `NodePackage.evaluators` 是**函数**、`NodePackage.editors` 里带**组件**，
 * JSON 里都放不下。所以数据包只能用 `code` 源码（求值逻辑）；
 * 要写 TS 函数或者贡献 UI，就得是**编译进主程序的代码包**。
 * 如果文件里出现了这两者之一，说明作者搞混了两种包 ——
 * 明确报出来，而不是静默忽略（静默忽略的表现是「我明明写了求值器 / 编辑器，
 * 为什么没生效」）。
 */
import { assetProblems, type GroupAssetDef } from './asset'
import type { NodePackage } from './package'

export const PACKAGE_FORMAT = 'node-print.package'
export const PACKAGE_FORMAT_VERSION = 1

export interface PackageFile {
  format: typeof PACKAGE_FORMAT
  version: number
  /** 谁写的、什么用途 —— 给人看的 */
  note?: string
  package: NodePackage
}

export type ParsePackageResult =
  | { ok: true; file: PackageFile }
  | { ok: false; problems: string[] }

/**
 * 节点类型标识的合法形状。
 *
 * ⚠️ 允许点（`包名.节点名`），因为**领域包的类型名本来就带命名空间** ——
 * 内核的边界检查也靠这个点来区分「领域节点」和「通用词」。
 * 不允许空格 / 引号 / 路径分隔符：它们会被写进文档、当作 Map 的键、
 * 出现在诊断里，形状不干净会让那些地方都要做防御。
 */
const TYPE_ID = /^[A-Za-z][A-Za-z0-9_-]*(\.[A-Za-z][A-Za-z0-9_-]*)*$/

/** 行的形状：只查**够注册表用**的那几个字段，不做完整 schema 校验 */
function checkRows(rows: unknown, where: string, problems: string[]): void {
  if (!Array.isArray(rows)) {
    problems.push(`${where}：rows 必须是数组`)
    return
  }
  const KINDS = ['output', 'property', 'input', 'repeater', 'group', 'display']
  rows.forEach((row, index) => {
    const at = `${where} 的第 ${index + 1} 行`
    if (typeof row !== 'object' || row === null) {
      problems.push(`${at}：必须是对象`)
      return
    }
    const kind = (row as { kind?: unknown }).kind
    if (typeof kind !== 'string' || !KINDS.includes(kind)) {
      problems.push(`${at}：kind 必须是 ${KINDS.join(' / ')} 之一（实际 ${JSON.stringify(kind)}）`)
      return
    }
    const id = (row as { id?: unknown }).id
    if (typeof id !== 'string' || id === '') {
      problems.push(`${at}：缺少 id`)
    }
    /* 端口行必须有 socket —— 没有端口的 output 行是接线时的空指针 */
    if (kind === 'output' || kind === 'input') {
      const socket = (row as { socket?: unknown }).socket
      if (typeof socket !== 'object' || socket === null) {
        problems.push(`${at}：${kind} 行必须有 socket`)
      } else if (typeof (socket as { type?: unknown }).type !== 'string') {
        problems.push(`${at}：socket 缺少 type`)
      }
    }
    if (kind === 'repeater') {
      /*
       * ⚠️ 变参行的两个字段必须校验，否则写错了**不报错、只静静地不对**：
       *   · `socketType` 缺了 → 每个实例的类型是 `undefined`，类型检查形同虚设；
       *   · `side` 拼错（`"outupt"`）→ 当成输入方向，端口跑到另一侧、
       *     而且「输出不留空槽」那条规则也就不生效了。
       * 校验期能查出来的别留给运行时。
       */
      if (typeof (row as { socketType?: unknown }).socketType !== 'string') {
        problems.push(`${at}：变参行必须有 socketType`)
      }
      const side = (row as { side?: unknown }).side
      if (side !== undefined && side !== 'input' && side !== 'output') {
        problems.push(
          `${at}：变参行的 side 只能是 'input' 或 'output'（实际 ${JSON.stringify(side)}）`,
        )
      }
    }
  })
}

/**
 * 查一个节点定义。⚠️ **只往 `problems` 里加**，不返回校验过的对象 ——
 * 第一版返回了 `NodeTypeDef`，但那个返回值既没用上、又让
 * 「本节点有没有问题」和「整个文件有没有问题」混在一起（读起来像两个意思）。
 */
function checkNodeType(raw: unknown, index: number, problems: string[]): void {
  const where = `第 ${index + 1} 个节点`
  if (typeof raw !== 'object' || raw === null) {
    problems.push(`${where}：必须是对象`)
    return
  }
  const def = raw as Record<string, unknown>

  if (typeof def.type !== 'string' || !TYPE_ID.test(def.type)) {
    problems.push(
      `${where}：type 必须是「名字」或「命名空间.名字」（字母开头，可含 . _ -），实际 ${JSON.stringify(def.type)}`,
    )
  }
  if (typeof def.title !== 'string' || def.title === '') problems.push(`${where}：缺少 title`)
  if (typeof def.category !== 'string' || def.category === '') {
    problems.push(`${where}：缺少 category`)
  }
  if (typeof def.width !== 'number' || !Number.isFinite(def.width) || def.width <= 0) {
    problems.push(`${where}：width 必须是正数`)
  }
  if (def.height !== undefined && (typeof def.height !== 'number' || def.height <= 0)) {
    problems.push(`${where}：height 给了就必须是正数`)
  }
  if (!Array.isArray(def.keywords)) {
    problems.push(`${where}：keywords 必须是数组（可以是空的）`)
  }
  checkRows(def.rows, where, problems)

  if (def.code === undefined) {
    /*
     * 没有 code 也没有求值器 → 这个节点算不出东西。
     * ⚠️ 不直接判错：纯编辑器构造（框 / 注释）本来就不求值。
     * 但要**提醒** —— 忘了写 code 和有意为之，从文件上分不出来。
     * 这里只在两者都没有、又不是 editorOnly 时给一条提示。
     */
    if (def.editorOnly !== true) {
      problems.push(
        `${where}：既没有 code 也没有标记 editorOnly —— 它永远不会产出值。` +
          `纯编辑器构造请标 editorOnly: true`,
      )
    }
  } else {
    const code = def.code as Record<string, unknown>
    if (code.dialect !== 'expr' && code.dialect !== 'js') {
      problems.push(`${where}：code.dialect 必须是 'expr' 或 'js'（实际 ${JSON.stringify(code.dialect)}）`)
    }
    if (typeof code.source !== 'string' || code.source.trim() === '') {
      problems.push(`${where}：code.source 必须是非空字符串`)
    }
  }
}

/**
 * 解析并校验一个包文件。
 *
 * ⚠️ 返回**问题清单**而不是抛异常：导入方要把它们显示给人看。
 * 一条一条列出来（而不是「格式错误」），人才知道改哪儿。
 *
 * ⚠️ `options.builtIn`：**只有随程序发的内置包**才能声明 `kind: 'base'`。
 * 外部导入的文件只能是 `domain`（可选加载）—— 否则用户能塞一个
 * 「基础包」进来，而基础包是**不可卸载**的：那等于让他把东西钉在内核上。
 * 判据由调用方给（只有装配点调它时会传 `builtIn: true`）。
 */
export function parsePackageFile(
  input: unknown,
  options: { builtIn?: boolean } = {},
): ParsePackageResult {
  const problems: string[] = []

  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return { ok: false, problems: ['文件根必须是一个对象'] }
  }
  const file = input as Record<string, unknown>

  if (file.format !== PACKAGE_FORMAT) {
    problems.push(`format 必须是 "${PACKAGE_FORMAT}"（实际 ${JSON.stringify(file.format)}）`)
  }
  if (file.version !== PACKAGE_FORMAT_VERSION) {
    problems.push(
      `version 必须是 ${PACKAGE_FORMAT_VERSION}（实际 ${JSON.stringify(file.version)}）——` +
        `版本对不上说明格式已经变了，不要硬加载`,
    )
  }

  const pack = file.package
  if (typeof pack !== 'object' || pack === null || Array.isArray(pack)) {
    problems.push('缺少 package 对象')
    return { ok: false, problems }
  }
  const p = pack as Record<string, unknown>

  if (typeof p.id !== 'string' || !TYPE_ID.test(p.id)) {
    problems.push(`package.id 不合法：${JSON.stringify(p.id)}`)
  }
  if (p.id === 'core') {
    problems.push('package.id 不能是 "core" —— 那是内核包，不允许被覆盖')
  }
  if (typeof p.label !== 'string' || p.label === '') problems.push('package.label 不能为空')
  /*
   * ⚠️ `kind` 有三档，但**文件里只允许两档**：
   *   · `base`   —— 随程序发的基础能力（函数节点…），**永远启用、不可卸载**；
   *   · `domain` —— 可选加载（数据源、打印域…），能装能卸。
   * `core` 是内核自己的位置，谁都别想占；
   * 而 `base` 只有**内置包**能声明（见 `options.builtIn` 的注释）——
   * 用户导入一个"不可卸载的包"是说不通的事。
   */
  const allowedKinds = options.builtIn ? ['base', 'domain'] : ['domain']
  if (typeof p.kind !== 'string' || !allowedKinds.includes(p.kind)) {
    problems.push(
      `package.kind 必须是 ${allowedKinds.map((k) => `"${k}"`).join(' 或 ')}` +
        `（实际 ${JSON.stringify(p.kind)}）` +
        (options.builtIn
          ? ''
          : ' —— 外部包只能是 "domain"（可选加载）；"base" 是内置基础能力，' +
            '"core" 是内核，两者都不允许由导入文件声明'),
    )
  }
  if (p.evaluators !== undefined) {
    problems.push(
      '数据包不能带 evaluators（函数没法序列化）。' +
        '求值逻辑请写进节点的 code.source；要写 TS 函数就得是编译进主程序的代码包。',
    )
  }
  if (p.editors !== undefined) {
    /*
     * ⚠️ 和 `evaluators` 同一条界线：**贡献 UI 的必须是代码包**。
     * 一个编辑器贡献里带着组件（活的东西），JSON 里放不下 —— 一条
     * 没有组件的编辑器贡献会开出一个空白窗口，而用户会以为界面坏了。
     * 所以是**明确拒绝**，不是静默忽略（静默忽略的表现是
     * 「我明明写了 editors，为什么窗口类型菜单里没有」）。
     */
    problems.push(
      '数据包不能带 editors（编辑器贡献必须来自代码包）。' +
        '它要带的是一个组件，JSON 里放不下 —— 请把编辑器写在编译进主程序的包里（见 types/editor.ts）。',
    )
  }

  if (!Array.isArray(p.categories) || p.categories.length === 0) {
    problems.push('package.categories 必须是非空数组')
  } else {
    p.categories.forEach((raw, index) => {
      const where = `第 ${index + 1} 个分类`
      if (typeof raw !== 'object' || raw === null) {
        problems.push(`${where}：必须是对象`)
        return
      }
      const c = raw as Record<string, unknown>
      if (typeof c.id !== 'string' || !TYPE_ID.test(c.id)) problems.push(`${where}：id 不合法`)
      if (typeof c.label !== 'string' || c.label === '') problems.push(`${where}：缺少 label`)
    })
  }

  if (!Array.isArray(p.nodeTypes) || p.nodeTypes.length === 0) {
    problems.push('package.nodeTypes 必须是非空数组')
  } else {
    p.nodeTypes.forEach((raw, index) => checkNodeType(raw, index, problems))

    /* 包内自查：类型标识不能重复（注册表也会挡，但那时报的错没有「第几个」） */
    const seen = new Set<string>()
    for (const raw of p.nodeTypes as { type?: unknown }[]) {
      const type = typeof raw?.type === 'string' ? raw.type : ''
      if (!type) continue
      if (seen.has(type)) problems.push(`包内节点类型重复：${type}`)
      seen.add(type)
    }

    /* 节点用到的分类必须在本包里声明过（否则菜单里会显示成英文 id） */
    const declared = new Set(
      Array.isArray(p.categories)
        ? (p.categories as { id?: unknown }[]).map((c) => (typeof c?.id === 'string' ? c.id : ''))
        : [],
    )
    for (const raw of p.nodeTypes as { type?: unknown; category?: unknown }[]) {
      const category = typeof raw?.category === 'string' ? raw.category : ''
      if (category && !declared.has(category)) {
        problems.push(`节点「${String(raw?.type)}」用了没声明的分类「${category}」`)
      }
    }
  }

  /* ── 资产 ── */
  if (p.assets !== undefined) {
    if (!Array.isArray(p.assets)) {
      problems.push('package.assets 必须是数组')
    } else {
      const seenAssets = new Set<string>()
      p.assets.forEach((raw, index) => {
        const where = `第 ${index + 1} 个资产`
        if (typeof raw !== 'object' || raw === null) {
          problems.push(`${where}：必须是对象`)
          return
        }
        const asset = raw as Record<string, unknown>
        if (typeof asset.id !== 'string' || !TYPE_ID.test(asset.id)) {
          problems.push(`${where}：id 不合法：${JSON.stringify(asset.id)}`)
        } else {
          if (seenAssets.has(asset.id)) problems.push(`包内资产 id 重复：${asset.id}`)
          seenAssets.add(asset.id)
        }
        if (typeof asset.label !== 'string' || asset.label === '') {
          problems.push(`${where}：缺少 label`)
        }
        if (typeof asset.category !== 'string' || asset.category === '') {
          problems.push(`${where}：缺少 category`)
        }
        if (!Array.isArray(asset.keywords)) problems.push(`${where}：keywords 必须是数组`)
        /* ⚠️ 端口名给了就得给够 —— 少一个会静默沿用「结果 N」 */
        if (asset.inputs !== undefined && !Array.isArray(asset.inputs)) {
          problems.push(`${where}：inputs 必须是字符串数组`)
        }
        if (asset.outputs !== undefined && !Array.isArray(asset.outputs)) {
          problems.push(`${where}：outputs 必须是字符串数组`)
        }
        /* 一张图**必须**有那两个接口节点，否则放出来是个没有端口的空壳 */
        const assetProblemsList = assetProblems(raw as never)
        problems.push(...assetProblemsList.map((m) => `${where}：${m}`))
      })
    }
  }

  if (problems.length > 0) return { ok: false, problems }
  return { ok: true, file: file as unknown as PackageFile }
}

/** 把一个包包成文件形状（导出用；内置包也走同一道校验） */
export function toPackageFile(pack: NodePackage, note?: string): PackageFile {
  return {
    format: PACKAGE_FORMAT,
    version: PACKAGE_FORMAT_VERSION,
    ...(note ? { note } : {}),
    package: pack,
  }
}

/** 文件名建议：包 id + 后缀（导出时用） */
export function packageFileName(pack: NodePackage): string {
  return `${pack.id}.np-package.json`
}
